import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../utils/api";
import toast from "react-hot-toast";
import { Bell, Lock, Save } from "lucide-react";
import useFeature from "../../hooks/useFeature";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Friendly label/description derived from the dotted event key, so we don't have
// to duplicate the backend registry. Falls back to a humanized key.
const EVENT_LABELS = {
  "payment.received.merchant": {
    label: "New payment received",
    description: "When a customer payment is confirmed on-chain",
  },
  "payment.partial.merchant": {
    label: "Partial payment alert",
    description: "When a payment covers only part of the amount due",
  },
  "payment.failed.merchant": {
    label: "Payment failed alert",
    description: "When an incoming payment fails",
  },
  "withdrawal.successful": {
    label: "Withdrawal successful",
    description: "When a payout to your wallet completes",
  },
  "withdrawal.failed": {
    label: "Withdrawal failed",
    description: "When a payout to your wallet fails",
  },
  "report.daily.summary": {
    label: "Daily transaction summary",
    description: "A daily digest of your transactions",
  },
  "report.weekly.revenue": {
    label: "Weekly revenue report",
    description: "A weekly summary of your revenue",
  },
  "payment.large.alert": {
    label: "Large payment alert",
    description: "When an unusually large payment is received",
  },
  "balance.low.warning": {
    label: "Low balance warning",
    description: "When your gas/operating balance runs low",
  },
  "token.chain.added": {
    label: "Token or chain added",
    description: "When a new token or network becomes available to you",
  },
  "account.api_key.generated": {
    label: "API key generated",
    description: "When a new API key is created for your account",
  },
  "account.team.invited": {
    label: "Team member invited",
    description: "When a teammate is invited to your account",
  },
  "billing.plan.upgraded": {
    label: "Plan upgrade confirmation",
    description: "When your plan is upgraded",
  },
  "billing.plan.renewal_reminder": {
    label: "Plan renewal reminder",
    description: "Before your plan renews",
  },
  "billing.payment_method.expiring": {
    label: "Payment method expiring",
    description: "When your billing payment method is about to expire",
  },
  "engagement.premium.nudge": {
    label: "Premium upgrade tips",
    description: "Occasional suggestions about Premium features",
  },
  "engagement.feature.announcement": {
    label: "Feature announcements",
    description: "News about new MaxelPay features",
  },
  "engagement.inactivity.reminder": {
    label: "Inactivity reminders",
    description: "A nudge if your account goes quiet",
  },
};

// Critical security emails cannot be opted out of. The preferences API omits them,
// so we surface them here as always-on (locked) toggles for transparency.
const CRITICAL_CATEGORIES = [
  { label: "New device login", description: "When your account is accessed from a new device or IP" },
  { label: "Two-factor authentication changes", description: "When 2FA is enabled or disabled" },
  { label: "Account suspension warning", description: "If your account is suspended" },
  { label: "Password changed", description: "When your account password is changed" },
  { label: "Email changed", description: "When your account email is changed" },
  { label: "Payout address change requested", description: "When a payout address change is requested" },
];

const humanize = (eventType) =>
  eventType
    .split(".")
    .join(" ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const labelFor = (eventType) =>
  EVENT_LABELS[eventType] || { label: humanize(eventType), description: "" };

const Toggle = ({ checked, disabled, onChange }) => (
  <label className={`notif-toggle ${disabled ? "is-disabled" : ""}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
    />
    <span className="notif-track">
      <span className="notif-thumb" />
    </span>
  </label>
);

const NotificationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState([]);
  const [dirty, setDirty] = useState(false);

  // The two scheduled report emails are Premium-gated; lock their toggles for Free.
  const canDaily = useFeature("daily_summary_email");
  const canWeekly = useFeature("weekly_report_email");
  const isLocked = (p) =>
    p.premiumOnly &&
    (p.eventType === "report.daily.summary"
      ? !canDaily
      : p.eventType === "report.weekly.revenue"
      ? !canWeekly
      : false);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchant/notifications/preferences`
      );
      const data = await response.json();
      if (data.success) {
        setPrefs(data.data || []);
      } else {
        toast.error(data.message || "Failed to load notification preferences");
      }
    } catch (error) {
      toast.error("Failed to load notification preferences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const handleToggle = (eventType, enabled) => {
    setPrefs((prev) =>
      prev.map((p) =>
        p.eventType === eventType && !isLocked(p) ? { ...p, enabled } : p
      )
    );
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchant/notifications/preferences`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            preferences: prefs.map((p) => ({
              eventType: p.eventType,
              enabled: p.enabled,
            })),
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success("Notification preferences saved");
        setDirty(false);
      } else {
        toast.error(data.message || "Failed to save preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="notif-tab">
      <div className="notif-card">
        <div className="notif-card-header">
          <Bell size={24} />
          <div>
            <h2>Email Notifications</h2>
            <p>Choose which emails you want to receive from MaxelPay</p>
          </div>
          {dirty && (
            <button
              className="notif-btn-primary header-action"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={16} />
              {saving ? "Saving..." : "Save changes"}
            </button>
          )}
        </div>

        {loading ? (
          <p className="notif-muted">Loading...</p>
        ) : prefs.length === 0 ? (
          <p className="notif-muted">No notification categories available.</p>
        ) : (
          <div className="notif-list">
            {prefs.map((p) => {
              const { label, description } = labelFor(p.eventType);
              const locked = isLocked(p);
              return (
                <div key={p.eventType} className="notif-item">
                  <div className="notif-info">
                    <span className="notif-name">
                      {label}
                      {p.premiumOnly && (
                        <span className="notif-badge">Premium</span>
                      )}
                    </span>
                    {description && (
                      <p className="notif-muted">{description}</p>
                    )}
                    {locked && (
                      <p className="notif-muted">
                        Upgrade to Premium to enable this report.
                      </p>
                    )}
                  </div>
                  <Toggle
                    checked={locked ? false : !!p.enabled}
                    disabled={locked}
                    onChange={(enabled) => handleToggle(p.eventType, enabled)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="notif-card">
        <div className="notif-card-header">
          <Lock size={24} />
          <div>
            <h2>Security Alerts</h2>
            <p>Always on — these critical emails cannot be turned off</p>
          </div>
        </div>

        <div className="notif-list">
          {CRITICAL_CATEGORIES.map((c) => (
            <div key={c.label} className="notif-item">
              <div className="notif-info">
                <span className="notif-name">{c.label}</span>
                <p className="notif-muted">{c.description}</p>
              </div>
              <Toggle checked disabled />
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .notif-tab {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .notif-card {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          box-shadow: var(--sh-sm);
          padding: 22px 24px;
        }
        .notif-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
          color: var(--accent);
          flex-wrap: wrap;
        }
        .notif-card-header h2 {
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
          margin: 0;
        }
        .notif-card-header p {
          font-size: 13px;
          color: var(--ink-secondary);
          margin: 2px 0 0;
        }
        .header-action {
          margin-left: auto;
        }
        .notif-muted {
          font-size: 13px;
          color: var(--ink-muted);
          margin: 4px 0 0;
        }
        .notif-list {
          display: flex;
          flex-direction: column;
        }
        .notif-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 14px 0;
          border-bottom: 1px solid var(--line);
        }
        .notif-item:last-child {
          border-bottom: none;
        }
        .notif-info {
          min-width: 0;
        }
        .notif-name {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
        }
        .notif-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: var(--r-pill);
          background: var(--accent-soft);
          color: var(--accent-ink);
        }
        .notif-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 16px;
          border-radius: var(--r-sm);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          background: var(--primary);
          color: #fff;
          white-space: nowrap;
        }
        .notif-btn-primary:hover:not(:disabled) {
          background: var(--primary-hover);
        }
        .notif-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .notif-toggle {
          position: relative;
          display: inline-flex;
          flex-shrink: 0;
          width: 44px;
          height: 26px;
          cursor: pointer;
        }
        .notif-toggle.is-disabled {
          cursor: not-allowed;
        }
        .notif-toggle input {
          position: absolute;
          opacity: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          cursor: inherit;
        }
        .notif-track {
          position: relative;
          width: 44px;
          height: 26px;
          border-radius: var(--r-pill);
          background: var(--line-strong);
          transition: background 0.2s ease;
        }
        .notif-thumb {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #fff;
          box-shadow: var(--sh-sm);
          transition: transform 0.2s ease;
        }
        .notif-toggle input:checked + .notif-track {
          background: var(--ok);
        }
        .notif-toggle input:checked + .notif-track .notif-thumb {
          transform: translateX(18px);
        }
        .notif-toggle.is-disabled .notif-track {
          opacity: 0.6;
        }
        @media (max-width: 576px) {
          .notif-card {
            padding: 16px;
          }
          .header-action {
            margin-left: 0;
            min-height: 44px;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationsTab;
